import { Modal, Loader, Flex, Stack, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { PREFERRED_PHARMACY_TEMPLATE_APPLICATION_EXTENSION } from '@bariendo/lib/extensions';
import { DOSESPOT_PATIENT_ID_SYSTEM, useDoseSpotIFrame } from '@medplum/dosespot-react';
import { useEffect, useMemo, useState } from 'react';
import { useMedplum } from '@medplum/react';
import dayjs from 'dayjs';
import { useTimelineContext } from '../hooks/useTimelineContext';
import styles from './MedicationIframeModal.module.css';
import { fetchBot } from '@/services/services';
import { alert } from '@/services/alert';

interface MedicationIframeModalProps {
  patientId: string | undefined;
  onDoseSpotSyncSuccess?: () => void;
}

export function MedicationIframeModal({ patientId, onDoseSpotSyncSuccess }: MedicationIframeModalProps) {
  const medplum = useMedplum();
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<React.ReactNode>('');
  const [showedPreferredPharmacyRetryNotice, setShowedPreferredPharmacyRetryNotice] = useState(false);
  const { medicationsContext, patientContext } = useTimelineContext();
  const { showDoseSpotIFrame, setShowDoseSpotIFrame } = medicationsContext ?? {};
  const patient = patientContext?.patient;

  const shouldShowPreferredPharmacyRetryNotice = useMemo(() => {
    if (!patient) {
      return false;
    }

    const hasDoseSpotId = Boolean(
      patient.identifier?.some(
        (identifier) => identifier.system === DOSESPOT_PATIENT_ID_SYSTEM && Boolean(identifier.value)
      )
    );
    const hasPreferredPharmacyTemplateApplied = Boolean(
      patient.extension?.some((extension) => extension.url === PREFERRED_PHARMACY_TEMPLATE_APPLICATION_EXTENSION)
    );

    return hasDoseSpotId && !hasPreferredPharmacyTemplateApplied;
  }, [patient]);

  const iframeUrl = useDoseSpotIFrame({
    patientId,
    onIframeSuccess: () => {
      setIsLoading(false);
      onDoseSpotSyncSuccess?.();
    },
    onError: (err) => {
      setIsLoading(false);

      if (err instanceof Error && err.message.includes('Bad Request')) {
        setErrorMessage(
          <Stack gap="xs">
            <Text fw={700}>Check missing or invalid fields:</Text>
            <Text>- Email, phone (10 digits), DOB, first and last name, address.</Text>
            <Text mt="1rem">
              <strong>Please contact an Orbit admin</strong> if any fields are incorrect or missing.
            </Text>
          </Stack>
        );
        return;
      }

      setErrorMessage(
        `An unexpected error occurred when syncing patient data with DoseSpot.
        Please contact an Orbit admin if the problem persists.`
      );
    },
  });
  const isDoseSpotModalOpen = (showDoseSpotIFrame ?? false) && !!iframeUrl;

  useEffect(() => {
    if (showDoseSpotIFrame && errorMessage) {
      notifications.show({
        title: "Couldn't sync patient data with DoseSpot.",
        message: (
          <Stack gap="xs">{typeof errorMessage === 'string' ? <Text>{errorMessage}</Text> : errorMessage}</Stack>
        ),
        color: 'red',
        autoClose: 15000,
      });
      setShowDoseSpotIFrame?.(false);
    }
  }, [showDoseSpotIFrame, errorMessage, setShowDoseSpotIFrame]);

  useEffect(() => {
    if (!isDoseSpotModalOpen) {
      setShowedPreferredPharmacyRetryNotice(false);
      return;
    }

    if (!shouldShowPreferredPharmacyRetryNotice || showedPreferredPharmacyRetryNotice) {
      return;
    }

    alert.warning({
      title: 'Preferred pharmacies might still be syncing.',
      message:
        'If preferred pharmacies (mail-order pharmacies included) do not show up in DoseSpot, please close and try opening it again.',
      options: { autoClose: 10000 },
    });
    setShowedPreferredPharmacyRetryNotice(true);
  }, [isDoseSpotModalOpen, shouldShowPreferredPharmacyRetryNotice, showedPreferredPharmacyRetryNotice]);

  /**
   * Call the DoseSpot prescriptions sync bot to sync the prescriptions for the patient
   * after the modal is closed.
   *
   * Uses a 1 hour window to sync the prescriptions.
   */
  const callSyncBot = async () => {
    const startDate = dayjs().subtract(1, 'hour').toISOString();
    const endDate = dayjs().toISOString();

    await fetchBot(medplum, 'sync-prescriptions-wrapper', {
      patientId,
      start: startDate,
      end: endDate,
    });
  };

  const handleModalClose = () => {
    setShowDoseSpotIFrame?.(false);
    callSyncBot();
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <Flex justify="center" align="center" h="70vh">
          <Loader />
        </Flex>
      );
    }

    return iframeUrl && <iframe src={iframeUrl} className={styles.dosespotIframe} title="DoseSpot IFrame" />;
  };

  return (
    <Modal
      opened={(showDoseSpotIFrame ?? false) && !!iframeUrl}
      onClose={handleModalClose}
      size="100%"
      padding={0}
      classNames={{
        content: styles.dosespotModalContent,
        body: styles.dosespotModalBody,
        header: styles.dosespotModalHeader,
        title: styles.dosespotModalTitle,
        close: styles.dosespotModalClose,
      }}
    >
      {renderContent()}
    </Modal>
  );
}
